import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Card,
  CardContent,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Info as InfoIcon,
  Place as PlaceIcon,
  LocalShipping as ShippingIcon,
} from "@mui/icons-material";
import { EventResponseDto } from "../services/Dtos/EventsDtos";
import { getEventTypeHexColor } from "../helpers/eventTypeColors";

interface EventsResultsProps {
  events: EventResponseDto[];
  loading?: boolean;
}

// Format date to readable format
const formatDateTime = (dateString?: string): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

// Format address for display
const formatAddress = (
  street1: string,
  street2: string | undefined,
  city: string,
  state: string,
  zipCode: string
): string => {
  const parts = [street1, street2, `${city}, ${state} ${zipCode}`].filter(Boolean);
  return parts.join(", ");
};

const EventsResults: React.FC<EventsResultsProps> = ({ events, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              Loading events...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No events found. Try adjusting your filters.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Events Results
          </Typography>
          <Chip
            label={`${events.length} event${events.length !== 1 ? "s" : ""} found`}
            color="primary"
            size="small"
          />
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" className="events-table">
            <TableHead>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Event Type</strong></TableCell>
                <TableCell><strong>Event Time</strong></TableCell>
                <TableCell><strong>Tracking #</strong></TableCell>
                <TableCell><strong>Company</strong></TableCell>
                <TableCell><strong>From Address</strong></TableCell>
                <TableCell><strong>From Metro</strong></TableCell>
                <TableCell><strong>Destination</strong></TableCell>
                <TableCell><strong>Destination Code</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>{event.id}</TableCell>
                  <TableCell>
                    <Chip
                      label={event.eventType.replace(/_/g, " ")}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        backgroundColor: getEventTypeHexColor(event.eventType),
                        color: '#fff',
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(event.eventTimestamp)}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <ShippingIcon
                        fontSize="small"
                        sx={{ color: getEventTypeHexColor(event.eventType) }}
                      />
                      <Typography variant="body2">{event.trackingNumber}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`API Key: ${event.company.apiKey.substring(0, 10)}...`}>
                      <Typography variant="body2">{event.company.name}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {event.addressFrom ? (
                      <Box display="flex" alignItems="flex-start" gap={0.5}>
                        <PlaceIcon
                          fontSize="small"
                          sx={{
                            mt: 0.3,
                            color: getEventTypeHexColor(event.eventType)
                          }}
                        />
                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                          {formatAddress(
                            event.addressFrom.street1,
                            event.addressFrom.street2,
                            event.addressFrom.city,
                            event.addressFrom.state,
                            event.addressFrom.zipCode
                          )}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                   <TableCell>
                    <Typography variant="body2">{event.addressFrom?.metroCode}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="flex-start" gap={0.5}>
                      <PlaceIcon
                        fontSize="small"
                        sx={{
                          mt: 0.3,
                          color: getEventTypeHexColor(event.eventType)
                        }}
                      />
                      <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                        {formatAddress(
                          event.addressTo.street1,
                          event.addressTo.street2,
                          event.addressTo.city,
                          event.addressTo.state,
                          event.addressTo.zipCode
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{event.addressTo?.metroCode}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Showing {events.length} result{events.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </CardContent>
  );
};

export default EventsResults;
